import {
  isAppLocal,
  checkLang,
  apiBaseUrl,
  dataFeedsBaseUrl,
  cmsBaseUrl,
  cmsBaseUrlV2,
} from '../helpers';
import {
  FEED_URLSRC_DF,
  FEED_URLSRC_DATA,
  FEED_URLSRC_CMS,
  FEED_URLSRC_CAPI,
  FEED_URLSRC_EMAIL_VERIFY,
  VERIFICATION_URL,
  FEED_URLSRC_CYSILL,
  CYSILL_URL,
  FETCH_DELAY_MIN,
  localStorageFetchDelay,
  FEED_URLSRC_CMS_V2,
  FEED_URLSRC_PASS_BREACH,
  PWNED_PASSWORDS_URL,
} from '../constants';
import { myS4CBaseUrl } from '../mys4c_helpers';

export function globalFetch({
  urlPath = '',
  urlSrc = FEED_URLSRC_DF,
  urlOverride = '',
  method = 'GET',
  dispatchSuccess = '',
  reducerIsLoading = '',
  reducerError = '',
  extraParams = '',
  extraParamsLang = true,
  isAlreadyInState = '',
  isAlreadyLoading = '',
}) {
  /*
	urlPath: the string for the feed eg listings_featured
	urlSrc: 0 = s4c.cymru/df/ 1 = s4c.cymru/clic/s4cplayer/data_feeds/ 2 = s4c.cymru/api/0_0_1/ 3 = s4c.cymru/capi/
	urlOverride: pass a full url with https and will override the custom url genarator
	method: pass over method POST, GET etc
	dispatchSuccess: Function to call on success
	reducerIsLoading: the reducer to use when data is loading
	reducerError: the reducer to use when not successful
	extraParams: extra paramaters exluding language eg &programme_id=800231755&show_prog_in_series=Y
	extraParamsLang: set if you would like to add lang to the url - default to true
	testForCache: Function to check for a cached version
	*/

  function generateUrl(urlPath, urlSrc, urlOverride) {
    let url, addLang;
    switch (urlSrc) {
      case FEED_URLSRC_DATA:
        //S4C Data Feeds - s4c.cymru/clic/s4cplayer/data_feeds/
        url = `${dataFeedsBaseUrl()}${urlPath}.json`;
        break;
      case FEED_URLSRC_CMS:
        //CMS - s4c.cymru/api/0_0_1/
        url = isAppLocal()
          ? `${cmsBaseUrl()}${checkLang()}_${urlPath}.json`
          : `${cmsBaseUrl()}${urlPath}/?lang=${checkLang(
              null,
              1
            )}${extraParams}`;
        break;
      case FEED_URLSRC_CAPI:
        //Capi - s4c.cymru/capi/
        //Capi requests that does not comply with fetchMyS4CApiData in MYS$CActions
        url = `${myS4CBaseUrl()}/${urlPath}${extraParams}`;
        break;
      case FEED_URLSRC_PASS_BREACH:
        //Pwned password API
        url = `${PWNED_PASSWORDS_URL}${extraParams}`;
        break;
      case FEED_URLSRC_EMAIL_VERIFY:
        //Email verifications API
        url = `${VERIFICATION_URL}${extraParams}`;
        break;
      case FEED_URLSRC_CYSILL:
        //Email verifications API
        url = `${CYSILL_URL}${extraParams}`;
        break;
      case FEED_URLSRC_CMS_V2:
        //CMS V2 - /api/v2/clic/
        const extraParamsStart = extraParams.toString().includes('&')
          ? ''
          : extraParams;
        const extraParamsEnd = extraParams.toString().includes('&')
          ? extraParams
          : '';
        addLang = extraParamsLang ? `?lang=${checkLang()}` : '?';
        url = isAppLocal()
          ? `${cmsBaseUrlV2()}${checkLang()}_${urlPath}.json`
          : `${cmsBaseUrlV2()}${urlPath}/${extraParamsStart}${addLang}${extraParamsEnd}`;
        break;
      default:
        //S4C df Feeds - s4c.cymru/df/
        addLang = extraParamsLang ? `?lang=${checkLang()}` : '?';
        url = isAppLocal()
          ? `${apiBaseUrl()}${checkLang()}_${urlPath}.json`
          : `${apiBaseUrl()}${urlPath}${addLang}${extraParams}`;
        break;
    }

    return urlOverride.length > 0 ? urlOverride : url;
  }

  function errorHandler(dispatch, errorMessage, logErrorMessage) {
    window.console.log(errorMessage);
    dispatch({ type: reducerError, payload: true });
    if (window.logError) {
      const logError =
        logErrorMessage && logErrorMessage !== 'undefined'
          ? logErrorMessage
          : errorMessage;
      window.logError('globalFetch::' + urlPath, logError, 'globalActions.js');
    }
  }

  function reducerErrorMessage(getState, msg = '') {
    const strings = getState().uiStrings[getState().curLang];
    return 'GlobalFetch::ERROR::' + strings['genericError'] + ' (' + msg + ')';
  }

  function getStatePathsFromLS() {
    return localStorage.getItem(localStorageFetchDelay) &&
      Object.keys(JSON.parse(localStorage.getItem(localStorageFetchDelay)))
        .length > 0
      ? JSON.parse(localStorage.getItem(localStorageFetchDelay))
      : {};
  }
  function updateStatePathsToLS(paths) {
    localStorage.setItem(localStorageFetchDelay, JSON.stringify(paths));
  }
  function isStateFresh(urlPath, now, getPaths) {
    const datePath = new Date(getPaths[urlPath]);
    return datePath && now - datePath < FETCH_DELAY_MIN * 60 * 1000;
  }
  function updateStatePath(urlPath, now, getPaths) {
    if (getPaths[urlPath]) {
      delete getPaths[urlPath];
    }
    getPaths[urlPath] = now;
    updateStatePathsToLS(getPaths);
  }
  function returnJsonString(str) {
    try {
      return JSON.parse(str);
    } catch (e) {
      return false;
    }
  }

  return function (dispatch, getState) {
    // If the state function exists and it returns true then don't trigger new request as data already exists
    // and the calling function is happy to re-use it
    const now = new Date();
    const getPaths = getStatePathsFromLS();

    if (
      isAlreadyInState &&
      isAlreadyInState(getState) &&
      isStateFresh(urlPath, now, getPaths)
    ) {
      //console.log (urlPath + " USING STATE VERSION");
    } else if (isAlreadyLoading && isAlreadyLoading(getState)) {
      //console.log (urlPath + "IS ALREADY LOADING")
    } else {
      if (isAlreadyInState) {
        //console.log (urlPath + " FETCHING DATA");
        updateStatePath(urlPath, now, getPaths);
      }
      if (reducerIsLoading) dispatch({ type: reducerIsLoading, payload: true });
      if (reducerError) dispatch({ type: reducerError, payload: false });

      return fetch(generateUrl(urlPath, urlSrc, urlOverride), {
        method: method,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
        .then((response) => {
          if (reducerIsLoading)
            dispatch({ type: reducerIsLoading, payload: false });
          if (!response.ok) {
            throw Error(response.statusText);
          }
          return response;
        })
        .then((response) => response.text())
        .then(
          (item) => {
            let itemObj = item;
            if (urlSrc === FEED_URLSRC_EMAIL_VERIFY) {
              itemObj = returnJsonString(item);
            } else if (urlSrc === FEED_URLSRC_PASS_BREACH) {
              itemObj = item;
            } else {
              itemObj = JSON.parse(item);
            }
            if (itemObj.error && itemObj.error.length > 0) {
              //Catch error from s4c.cymru/df/
              errorHandler(
                dispatch,
                reducerErrorMessage(getState, itemObj.toString()),
                itemObj.error
              );
            } else if (itemObj) {
              //Success send the data on
              if (dispatchSuccess) dispatchSuccess(itemObj, dispatch);
            } else {
              //Catch the error if everything else fails
              errorHandler(
                dispatch,
                reducerErrorMessage(getState, itemObj.toString())
              );
            }
          },
          (error) => {
            if (reducerIsLoading)
              dispatch({ type: reducerIsLoading, payload: false });
            errorHandler(
              dispatch,
              reducerErrorMessage(getState, error.toString()),
              error.toString()
            );
          }
        );
    }
  };
}
